import { useState, useMemo, useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { BsSearch, BsUpload } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import RequireCapability from '#globalComponents/RequireCapability/RequireCapability';

import { useLeadData } from '../../../contexts/LeadDataContext';
import { useLeadJobs } from '../../../contexts/LeadJobContext';
import { hasAIScores as checkHasAIScores } from '../../../utils/leadDataTransform';
import CSVImportModal from '../../CSVImportModal';
import { LeadView } from '../LeadView/LeadView';
import TableFilterBar from '../LeadView/TableFilterBar';

import { JobProgressList } from './SubComponents/JobProgressList/JobProgressList';
import { LeadTableHeader } from './SubComponents/LeadTableHeader/LeadTableHeader';
import { LeadTableModals } from './SubComponents/LeadTableModals/LeadTableModals';

const LeadOverviewContainer = ({ onNewSearch, draftBadge, extraJobs, onResumeAssistant, onShowProtocol }) => {
	const [showExtendModal, setShowExtendModal] = useState(false);
	const [showCSVImportModal, setShowCSVImportModal] = useState(false);

	const {
		selectedSearchQuery,
		leads,
		totalCount,
		orderBy,
		orderDir,
		userHasSortedManually,
		handleSortChange,
		filters,
		handleFilterRemove,
		handleFiltersClearAll,
		loadingQueries,
		loadingLeads,
	} = useLeadData();

	const { runningJobs, hasRunningAnalysisJob } = useLeadJobs();

	// When an AI analysis produces scores, switch to AI-score sorting once per run —
	// but never override a sort the user has explicitly chosen.
	const hasAutoSortedRef = useRef(false);
	const hasAIScores = useMemo(() => checkHasAIScores(leads), [leads]);

	useEffect(() => {
		if (hasRunningAnalysisJob && hasAIScores && !userHasSortedManually && !hasAutoSortedRef.current) {
			handleSortChange('ai-score', 'desc');
			hasAutoSortedRef.current = true;
		}
		if (!hasRunningAnalysisJob) {
			hasAutoSortedRef.current = false;
		}
	}, [hasRunningAnalysisJob, hasAIScores, userHasSortedManually, handleSortChange]);

	// Primary row: entry points that create data, independent of the current selection.
	const primaryActions = (
		<div className="flex items-center gap-2">
			<Button
				data-tour="new-search"
				onClick={onNewSearch}
				variant="solid"
				size="md"
				className="relative"
				title={draftBadge ? `Unfertiges Gespräch mit dem Assistenten: ${draftBadge.name}` : undefined}
			>
				<BsSearch />
				Neue Suche
				{draftBadge && (
					<span
						className={`absolute -right-1 -top-1 h-3 w-3 rounded-full border-2 border-white bg-accent ${draftBadge.working ? 'animate-pulse' : ''}`}
						aria-label={`Entwurf: ${draftBadge.name}`}
					/>
				)}
			</Button>
			<RequireCapability required="csvimport">
				<Button
					variant="solid"
					size="md"
					onClick={() => setShowCSVImportModal(true)}
					className="flex items-center gap-2"
					title="Kontakte aus CSV-Datei importieren"
				>
					<BsUpload />
					CSV Import
				</Button>
			</RequireCapability>
		</div>
	);

	return (
		<>
			{/* Header with Query Selector and Actions */}
			<LeadTableHeader
				onExtend={() => setShowExtendModal(true)}
				actions={primaryActions}
				onResumeAssistant={onResumeAssistant}
				onShowProtocol={onShowProtocol}
			/>

			<JobProgressList runningJobs={runningJobs} extraJobs={extraJobs} />

			<TableFilterBar
				filters={filters}
				totalCount={totalCount}
				unfilteredCount={selectedSearchQuery?.companyCount}
				onRemoveFilter={handleFilterRemove}
				onClearAll={handleFiltersClearAll}
			/>

			<LeadView
				leads={leads}
				isLoading={loadingQueries || loadingLeads}
				orderBy={orderBy}
				orderDir={orderDir}
				onSortChange={handleSortChange}
			/>

			{/* Modals */}
			<LeadTableModals
				showExtendModal={showExtendModal}
				onCloseExtendModal={() => setShowExtendModal(false)}
				currentLeadCount={totalCount}
				queryId={selectedSearchQuery?.id}
				queryName={selectedSearchQuery?.queryName || ''}
				queryInput={selectedSearchQuery?.input || null}
				queryTypeCode={selectedSearchQuery?.queryTypes?.code || ''}
			/>

			{/* CSV Import Modal */}
			{showCSVImportModal && (
				<CSVImportModal isOpen={showCSVImportModal} onClose={() => setShowCSVImportModal(false)} />
			)}
		</>
	);
};

LeadOverviewContainer.propTypes = {
	onNewSearch: PropTypes.func.isRequired,
	draftBadge: PropTypes.shape({ name: PropTypes.string, working: PropTypes.bool }),
	extraJobs: PropTypes.arrayOf(PropTypes.object),
	onResumeAssistant: PropTypes.func,
	onShowProtocol: PropTypes.func,
};

export default LeadOverviewContainer;
